import { differenceInDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromDateString } from '@/common/utils/dateFormats';

export const TimelineTodayMarker = ({
  from,
  rowHeaderWidth,
  dayWidth,
  tableHeight,
  scrollContainerRef,
}: {
  from: string;
  rowHeaderWidth: number;
  dayWidth: number;
  tableHeight: number;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { t } = useTranslation();
  const [isTodayLineHidden, setIsTodayLineHidden] = useState(false);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }, []);

  const fromDate = useMemo(() => fromDateString(from), [from]);

  const daysFromStart = fromDate ? differenceInDays(today, fromDate) : 0;
  const todayPosition = rowHeaderWidth + daysFromStart * dayWidth;
  const shouldShowTodayLine = !!fromDate && daysFromStart >= 0;

  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    if (!scrollElement || !shouldShowTodayLine) return;

    const checkTodayLinePosition = () => {
      const scrollLeft = scrollElement.scrollLeft;
      const todayLineLeftEdge = todayPosition - scrollLeft;
      setIsTodayLineHidden(todayLineLeftEdge < rowHeaderWidth);
    };

    checkTodayLinePosition();
    scrollElement.addEventListener('scroll', checkTodayLinePosition);
    return () => scrollElement.removeEventListener('scroll', checkTodayLinePosition);
  }, [scrollContainerRef, shouldShowTodayLine, todayPosition, rowHeaderWidth]);

  if (!shouldShowTodayLine || todayPosition < rowHeaderWidth || isTodayLineHidden) {
    return null;
  }

  return (
    <div
      className="absolute top-0 w-0.5 pointer-events-none z-30"
      style={{
        left: `${todayPosition}px`,
        height: tableHeight,
        backgroundColor: '#ec8f90',
      }}
    >
      <div
        className="sticky top-1 px-2 py-0.5 rounded font-bold border text-[10px]"
        style={{
          backgroundColor: '#fff8f8',
          borderColor: '#ec8f90',
          color: '#cf2c31',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'fit-content',
        }}
      >
        {t(($) => $.common.today)}
      </div>
    </div>
  );
};
